import React, { useState, useEffect } from 'react';
import api from '../services/api.js';
import toast from 'react-hot-toast';
import { 
  Receipt, X, UploadCloud, FileText, CheckCircle, 
  ExternalLink, Trash2, AlertCircle, Building2, User 
} from 'lucide-react';

export default function SubirBoletaModal({ 
  item, 
  type = 'tramite', // 'tramite' | 'solicitud'
  isOpen, 
  onClose, 
  onSuccess 
}) {
  const [loading, setLoading] = useState(false);
  const [numeroBoleta, setNumeroBoleta] = useState('');
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [enviarCorreo, setEnviarCorreo] = useState(true);

  // Determinar cotización y datos según sea trámite o solicitud
  const cotizacion = type === 'tramite' 
    ? (item?.cotizaciones?.find(c => c.estado === 'pagada') || item?.cotizacion || item?.cotizaciones?.[0])
    : null;

  const currentNumeroBoleta = type === 'tramite' 
    ? (cotizacion?.numero_boleta || item?.numero_boleta || '') 
    : (item?.numero_boleta || '');

  const currentBoletaUrl = type === 'tramite' 
    ? (cotizacion?.boleta_url || (cotizacion?.boleta_path ? `/api/v1/tramites/${item.id}/boleta` : null))
    : (item?.boleta_url || (item?.boleta_path ? `/api/v1/solicitudes/${item.id}/boleta` : null));

  const tieneBoletaPrevia = Boolean(
    type === 'tramite' ? (cotizacion?.boleta_path || currentNumeroBoleta) : (item?.boleta_path || currentNumeroBoleta)
  );

  useEffect(() => {
    if (isOpen) {
      setNumeroBoleta(currentNumeroBoleta || '');
      setFile(null);
      setFilePreview(null);
    }
  }, [isOpen, currentNumeroBoleta]);

  if (!isOpen || !item) return null;

  // Datos de Facturación
  const esMismaPersona = item.facturacion_misma_persona === true || 
    (item.facturacion_misma_persona !== false && !item.boleta_razon_social && !item.boleta_giro);

  const rutFacturacion = item.boleta_rut || item.rut_boleta || item.rut_solicitante || '—';
  const nombreFacturacion = item.boleta_razon_social || item.nombre_boleta || item.nombre_solicitante || '—';
  const giro = item.boleta_giro || '—';
  const totalMonto = type === 'tramite' 
    ? (cotizacion?.total || item.total || 0)
    : (item.total_cotizacion || 0);

  const handleFileChange = (selectedFile) => {
    if (!selectedFile) return;

    // Validar tipo de archivo
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!validTypes.includes(selectedFile.type)) {
      toast.error('Formato inválido. Por favor seleccione un archivo PDF o una imagen (JPG, PNG, WEBP).');
      return;
    }

    // Validar tamaño (máximo 10MB)
    if (selectedFile.size > 10 * 1024 * 1024) {
      toast.error('El archivo excede el tamaño máximo permitido de 10 MB.');
      return;
    }

    setFile(selectedFile);

    if (selectedFile.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(selectedFile);
    } else {
      setFilePreview(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(e.dataTransfer.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file && !numeroBoleta.trim() && !tieneBoletaPrevia) {
      toast.error('Debe adjuntar el archivo de la boleta o ingresar el número de boleta.');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      if (file) {
        formData.append('boleta', file);
      }
      formData.append('numero_boleta', numeroBoleta.trim());
      formData.append('enviar_correo', enviarCorreo ? '1' : '0');

      if (type === 'tramite') {
        if (cotizacion?.id) {
          formData.append('cotizacion_id', cotizacion.id);
        }
        const res = await api.post(`/tramites/${item.id}/subir-boleta`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(res.data?.message || 'Boleta actualizada exitosamente.');
      } else {
        const res = await api.post(`/solicitudes/${item.id}/subir-boleta`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        toast.success(res.data?.message || 'Boleta actualizada exitosamente.');
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Error al subir boleta:', err);
      const msg = err.response?.data?.error || err.response?.data?.message || 'Error al guardar la boleta.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="modal-overlay" 
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '1rem',
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div 
        className="glass-card" 
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '560px',
          background: 'var(--bg-surface, #ffffff)',
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
          border: '1px solid var(--border-color, #e2e8f0)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh'
        }}
      >
        {/* Cabecera */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid var(--border-color, #e2e8f0)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(to right, rgba(2, 132, 199, 0.05), rgba(14, 165, 233, 0.02))'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #0284c7, #0369a1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              boxShadow: '0 4px 10px rgba(2, 132, 199, 0.3)'
            }}>
              <Receipt size={22} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-main, #0f172a)' }}>
                Subir / Actualizar Boleta
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--primary, #0284c7)' }}>
                  {item.numero_seguimiento}
                </span>
                <span style={{ fontSize: '0.78rem', color: 'var(--text-muted, #64748b)' }}>
                  · {item.nombre_solicitante}
                </span>
              </div>
            </div>
          </div>

          <button 
            type="button"
            onClick={onClose}
            className="icon-btn"
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--text-muted, #94a3b8)',
              padding: '6px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Cuerpo con Scroll */}
        <div style={{ padding: '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          
          {/* Ficha Resumen de Facturación */}
          <div style={{
            background: 'var(--bg-body, #f8fafc)',
            border: '1px solid var(--border-color, #e2e8f0)',
            borderRadius: '10px',
            padding: '0.85rem 1rem',
            fontSize: '0.82rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ fontWeight: 700, color: 'var(--text-main, #0f172a)', display: 'flex', alignItems: 'center', gap: 5 }}>
                {esMismaPersona ? <User size={13} color="#0284c7" /> : <Building2 size={13} color="#059669" />}
                Receptor Tributario:
              </span>
              {totalMonto > 0 && (
                <span style={{ fontWeight: 800, color: '#059669', fontSize: '0.9rem' }}>
                  Total: ${Number(totalMonto).toLocaleString('es-CL')}
                </span>
              )}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.5rem', color: 'var(--text-muted, #475569)' }}>
              <div><strong>RUT:</strong> <span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{rutFacturacion}</span></div>
              <div><strong>Razón Social:</strong> {nombreFacturacion}</div>
              {giro !== '—' && <div><strong>Giro:</strong> {giro}</div>}
            </div>
          </div>

          {/* Estado Actual de la Boleta */}
          {tieneBoletaPrevia ? (
            <div style={{
              background: '#ecfdf5',
              border: '1px solid #a7f3d0',
              borderRadius: '10px',
              padding: '0.85rem 1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <CheckCircle size={20} color="#059669" />
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#065f46' }}>
                    Boleta ya registrada
                    {currentNumeroBoleta ? ` (N° ${currentNumeroBoleta})` : ''}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#047857' }}>
                    {currentBoletaUrl ? 'Archivo adjunto disponible' : 'Solo número registrado'}
                  </div>
                </div>
              </div>

              {currentBoletaUrl && (
                <a
                  href={currentBoletaUrl}
                  target="_blank"
                  rel="noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    padding: '5px 12px',
                    background: '#ffffff',
                    color: '#059669',
                    border: '1px solid #a7f3d0',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                >
                  <ExternalLink size={13} /> Ver Actual
                </a>
              )}
            </div>
          ) : (
            <div style={{
              background: '#fffbeb',
              border: '1px solid #fde68a',
              borderRadius: '10px',
              padding: '0.75rem 1rem',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <AlertCircle size={18} color="#d97706" />
              <span style={{ fontSize: '0.82rem', color: '#92400e', fontWeight: 500 }}>
                Aún no se ha adjuntado la boleta electrónica para este pago.
              </span>
            </div>
          )}

          {/* Formulario */}
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            
            {/* Input Número de Boleta */}
            <div>
              <label style={{ display: 'block', fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-main, #1e293b)', marginBottom: '0.35rem' }}>
                Número de Boleta (Opcional)
              </label>
              <input
                type="text"
                value={numeroBoleta}
                onChange={(e) => setNumeroBoleta(e.target.value)}
                placeholder="Ej: 145920"
                style={{
                  width: '100%',
                  padding: '9px 12px',
                  fontSize: '0.9rem',
                  borderRadius: '8px',
                  border: '1px solid var(--border-color, #cbd5e1)',
                  background: 'var(--bg-body, #ffffff)',
                  color: 'var(--text-main, #0f172a)',
                  boxSizing: 'border-box',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--primary, #0284c7)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-color, #cbd5e1)'}
              />
              <span style={{ display: 'block', fontSize: '0.73rem', color: 'var(--text-muted, #64748b)', marginTop: '4px' }}>
                Número o folio asignado por el SII o sistema de facturación.
              </span>
            </div>

            {/* Zona de Carga de Archivo */}
            <div>
              <label style={{ display: 'block', fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-main, #1e293b)', marginBottom: '0.35rem' }}>
                Archivo de Boleta (PDF o Imagen)
              </label>

              {!file ? (
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  style={{
                    border: `2px dashed ${isDragging ? 'var(--primary, #0284c7)' : 'var(--border-color, #cbd5e1)'}`,
                    borderRadius: '12px',
                    padding: '1.5rem 1rem',
                    textAlign: 'center',
                    background: isDragging ? 'rgba(2, 132, 199, 0.05)' : 'var(--bg-body, #f8fafc)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => document.getElementById('boleta-file-input').click()}
                >
                  <input
                    id="boleta-file-input"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg,.webp"
                    style={{ display: 'none' }}
                    onChange={(e) => handleFileChange(e.target.files[0])}
                  />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      background: 'rgba(2, 132, 199, 0.1)',
                      color: 'var(--primary, #0284c7)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <UploadCloud size={20} />
                    </div>
                    <div>
                      <span style={{ fontSize: '0.88rem', fontWeight: 600, color: 'var(--primary, #0284c7)' }}>
                        Haga clic para subir
                      </span>
                      <span style={{ fontSize: '0.85rem', color: 'var(--text-muted, #64748b)' }}> o arrastre el archivo aquí</span>
                    </div>
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-muted, #94a3b8)' }}>
                      PDF, JPG, PNG o WEBP (Máx. 10 MB)
                    </span>
                  </div>
                </div>
              ) : (
                <div style={{
                  border: '1px solid #bfdbfe',
                  borderRadius: '10px',
                  padding: '0.85rem 1rem',
                  background: '#eff6ff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '12px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                    {filePreview ? (
                      <img 
                        src={filePreview} 
                        alt="Preview" 
                        style={{ width: '38px', height: '38px', objectFit: 'cover', borderRadius: '6px' }} 
                      />
                    ) : (
                      <div style={{
                        width: '38px',
                        height: '38px',
                        borderRadius: '6px',
                        background: '#dbeafe',
                        color: '#1d4ed8',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <FileText size={20} />
                      </div>
                    )}
                    <div style={{ overflow: 'hidden' }}>
                      <div style={{ 
                        fontSize: '0.85rem', 
                        fontWeight: 600, 
                        color: '#1e3a8a',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap'
                      }}>
                        {file.name}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#3b82f6' }}>
                        {(file.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      setFile(null);
                      setFilePreview(null);
                    }}
                    className="icon-btn"
                    title="Quitar archivo"
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#ef4444',
                      padding: '6px',
                      cursor: 'pointer',
                      borderRadius: '6px'
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>

            {/* Opción Enviar Correo */}
            {(item.correo || item.email) && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '0.4rem 0.2rem',
                userSelect: 'none'
              }}>
                <input
                  type="checkbox"
                  id="enviar-boleta-correo"
                  checked={enviarCorreo}
                  onChange={(e) => setEnviarCorreo(e.target.checked)}
                  style={{ width: '16px', height: '16px', accentColor: 'var(--primary, #0284c7)', cursor: 'pointer' }}
                />
                <label 
                  htmlFor="enviar-boleta-correo" 
                  style={{ fontSize: '0.84rem', color: 'var(--text-main, #334155)', cursor: 'pointer' }}
                >
                  Notificar al ciudadano por correo con la boleta adjunta ({item.correo || item.email})
                </label>
              </div>
            )}

            {/* Acciones */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '10px',
              paddingTop: '0.5rem',
              borderTop: '1px solid var(--border-color, #e2e8f0)'
            }}>
              <button
                type="button"
                onClick={onClose}
                className="btn btn-outline"
                disabled={loading}
                style={{ padding: '8px 16px', fontSize: '0.86rem' }}
              >
                Cancelar
              </button>
              
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
                style={{
                  padding: '8px 20px',
                  fontSize: '0.86rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  minWidth: '130px',
                  justifyContent: 'center'
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                    Guardando...
                  </>
                ) : (
                  <>
                    <Receipt size={15} />
                    {tieneBoletaPrevia ? 'Actualizar Boleta' : 'Guardar Boleta'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
